import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Download, RefreshCw, Store, Truck, Users } from 'lucide-react';
import api, { getBoutiqueVue } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatMontant } from '../../utils/format';
import { exporter } from '../../utils/exportRapport';
import DateRangePicker from '../../components/common/DateRangePicker';

// Libellé + icône + couleur par canal de vente
const CANAUX = {
  magasin:   { label: 'Magasin',   Icon: Store,  classe: 'bg-blue-500/15 text-blue-300 border-blue-600/40' },
  livraison: { label: 'Livraison', Icon: Truck,  classe: 'bg-purple-500/15 text-purple-300 border-purple-600/40' },
  terrain:   { label: 'Terrain',   Icon: Users,  classe: 'bg-emerald-500/15 text-emerald-300 border-emerald-600/40' },
};

const today = () => new Date().toISOString().split('T')[0];
const debutMois = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

function BadgeCanal({ canal }) {
  const c = CANAUX[canal] || { label: canal, Icon: Store, classe: 'bg-gray-700 text-gray-300 border-gray-600' };
  const { Icon } = c;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${c.classe}`}>
      <Icon size={13} />
      {c.label}
    </span>
  );
}

export default function JournalVentes() {
  // Le boutique_id du sélecteur global est injecté automatiquement dans chaque
  // requête GET par l'intercepteur axios (services/api.js) — rien à gérer ici.
  const { user } = useAuth();
  const estVendeur = user?.role === 'gerant_vendeur'; // ne voit que ses ventes
  const [debut, setDebut]     = useState(debutMois());
  const [fin, setFin]         = useState(today());
  const [canal, setCanal]     = useState('tous');
  const [data, setData]       = useState({ ventes: [], total_montant: 0, nb_ventes: 0 });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [ouverts, setOuverts] = useState(() => new Set());
  const [exportMsg, setExportMsg] = useState('');
  const [nbBoutiques, setNbBoutiques] = useState(1);

  // Combien de boutiques a ce commerce ? (pour décider d'afficher la colonne)
  useEffect(() => {
    api.get('/boutiques')
      .then(r => setNbBoutiques(Array.isArray(r.data) ? r.data.length : (r.data?.boutiques?.length || 1)))
      .catch(() => setNbBoutiques(1));
  }, []);

  // La colonne « Boutique » n'apparaît QUE si :
  //   - le commerce a plusieurs boutiques, ET
  //   - la vue courante est « Toutes » (aucune boutique précise sélectionnée), ET
  //   - on n'est pas un gérant/vendeur (verrouillé sur sa boutique).
  // Le sélecteur de boutique recharge la page (window.location.reload), donc
  // getBoutiqueVue() lu au montage est toujours à jour.
  const afficherBoutique = !estVendeur && nbBoutiques >= 2 && !getBoutiqueVue();
  const nbColonnes = afficherBoutique ? 7 : 6;

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur('');
    try {
      const params = { date_debut: debut, date_fin: fin, limit: 1000 };
      if (canal !== 'tous') params.canal = canal;
      const { data } = await api.get('/rapports/journal-ventes', { params });
      setData(data);
      setOuverts(new Set());
    } catch (e) {
      setErreur(e?.response?.data?.message || 'Impossible de charger le journal des ventes.');
    } finally {
      setLoading(false);
    }
  }, [debut, fin, canal]);

  useEffect(() => { charger(); }, [charger]);

  const toggle = (id) => {
    setOuverts(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  // Construit le rapport à exporter (Excel / PDF / CSV) via l'utilitaire existant.
  const construireRapport = () => ({
    titre: estVendeur ? 'Mes ventes' : 'Journal des ventes',
    periode: { debut, fin },
    sections: [{
      titre: `Ventes (${data.nb_ventes})`,
      colonnes: [
        { header: 'Date',     key: 'date' },
        { header: 'Heure',    key: 'heure' },
        { header: 'Canal',    key: 'canal' },
        ...(afficherBoutique ? [{ header: 'Boutique', key: 'boutique' }] : []),
        { header: 'Vendeur',  key: 'vendeur' },
        { header: 'Articles', key: 'articles' },
        { header: 'Montant',  key: 'montant' },
      ],
      lignes: data.ventes.map(v => ({
        date:    new Date(v.date).toLocaleDateString('fr-FR'),
        heure:   (v.heure || '').slice(0, 5),
        canal:   CANAUX[v.canal]?.label || v.canal,
        ...(afficherBoutique ? { boutique: v.boutique_nom || '—' } : {}),
        vendeur: v.operateur || '—',
        articles: (v.articles || []).map(a => `${a.libelle} x${a.quantite}`).join(', ') || '—',
        montant: formatMontant(v.montant),
      })),
    }],
  });

  const exporterVers = async (format) => {
    setExportMsg('');
    if (!data.ventes.length) { setExportMsg('Rien à exporter sur cette période.'); return; }
    await exporter(format, construireRapport(), (msg) => setExportMsg(msg));
  };

  const filtresCanal = [
    { id: 'tous',      label: 'Tous' },
    { id: 'magasin',   label: 'Magasin' },
    { id: 'livraison', label: 'Livraison' },
    { id: 'terrain',   label: 'Terrain' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">

        {/* En-tête */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">
              {estVendeur ? 'Mes ventes' : 'Journal des ventes'}
            </h1>
            <p className="text-sm text-gray-400">
              {estVendeur
                ? 'Vos propres ventes, dans l\'ordre de réalisation.'
                : 'Toutes les ventes — magasin, livraison et terrain — dans l\'ordre de réalisation.'}
            </p>
          </div>
          <button
            onClick={charger}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>

        {/* Filtres */}
        <div className="bg-gray-900 rounded-xl p-4 mb-4 space-y-3">
          <DateRangePicker debut={debut} fin={fin} onChange={(d, f) => { setDebut(d); setFin(f); }} />
          <div className="flex flex-wrap items-center gap-2">
            {filtresCanal.map(f => (
              <button
                key={f.id}
                onClick={() => setCanal(f.id)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  canal === f.id
                    ? 'bg-yellow-500 text-gray-900 border-yellow-500 font-semibold'
                    : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bandeau total + export */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="text-sm text-gray-300">
            <span className="font-semibold text-gray-100">{data.nb_ventes}</span> vente(s) —
            total <span className="font-semibold text-yellow-400">{formatMontant(data.total_montant)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 flex items-center gap-1"><Download size={13} /> Télécharger :</span>
            {['xlsx', 'pdf', 'csv'].map(fmt => (
              <button
                key={fmt}
                onClick={() => exporterVers(fmt)}
                className="px-2.5 py-1 rounded-md bg-gray-800 hover:bg-gray-700 text-xs uppercase"
              >
                {fmt}
              </button>
            ))}
          </div>
        </div>
        {exportMsg && <div className="text-xs text-yellow-400 mb-2">{exportMsg}</div>}
        {erreur && <div className="text-sm text-red-300 bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2 mb-3">{erreur}</div>}

        {/* Tableau */}
        <div className="bg-gray-900 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-800">
                  <th className="w-8 px-3 py-2"></th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Heure</th>
                  <th className="px-3 py-2">Canal</th>
                  {afficherBoutique && <th className="px-3 py-2">Boutique</th>}
                  <th className="px-3 py-2">Vendeur</th>
                  <th className="px-3 py-2 text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={nbColonnes} className="px-3 py-8 text-center text-gray-500">Chargement…</td></tr>
                )}
                {!loading && data.ventes.length === 0 && (
                  <tr><td colSpan={nbColonnes} className="px-3 py-8 text-center text-gray-500">Aucune vente sur cette période.</td></tr>
                )}
                {!loading && data.ventes.map(v => {
                  const ouvert = ouverts.has(v.id);
                  const articles = v.articles || [];
                  return (
                    <React.Fragment key={v.id}>
                      <tr
                        onClick={() => toggle(v.id)}
                        className="border-b border-gray-800/60 hover:bg-gray-800/40 cursor-pointer"
                      >
                        <td className="px-3 py-2 text-gray-500">
                          {ouvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{new Date(v.date).toLocaleDateString('fr-FR')}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-400">{(v.heure || '').slice(0, 5)}</td>
                        <td className="px-3 py-2"><BadgeCanal canal={v.canal} /></td>
                        {afficherBoutique && <td className="px-3 py-2 text-gray-300">{v.boutique_nom || '—'}</td>}
                        <td className="px-3 py-2 text-gray-300">{v.operateur || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{formatMontant(v.montant)}</td>
                      </tr>
                      {ouvert && (
                        <tr className="bg-gray-950/60">
                          <td></td>
                          <td colSpan={nbColonnes - 1} className="px-3 py-3">
                            <div className="text-xs text-gray-400 mb-2">{v.description}</div>
                            {articles.length === 0 ? (
                              <div className="text-xs text-gray-500 italic">
                                {v.canal === 'terrain'
                                  ? 'Vente terrain — détail calculé à la clôture (voir la page Vendeurs).'
                                  : 'Aucun détail d\'article disponible.'}
                              </div>
                            ) : (
                              <table className="w-full max-w-xl text-xs">
                                <thead>
                                  <tr className="text-gray-500 text-left">
                                    <th className="py-1 pr-4">Article</th>
                                    <th className="py-1 pr-4 text-right">Qté</th>
                                    <th className="py-1 pr-4 text-right">Prix unit.</th>
                                    <th className="py-1 text-right">Sous-total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {articles.map((a, i) => (
                                    <tr key={i} className="border-t border-gray-800/60">
                                      <td className="py-1 pr-4">{a.libelle || '—'}</td>
                                      <td className="py-1 pr-4 text-right">{a.quantite}</td>
                                      <td className="py-1 pr-4 text-right">{formatMontant(a.prix)}</td>
                                      <td className="py-1 text-right">{formatMontant(a.quantite * a.prix)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {!loading && data.ventes.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-gray-700 font-semibold">
                    <td colSpan={nbColonnes - 1} className="px-3 py-3 text-right text-gray-300">Total période</td>
                    <td className="px-3 py-3 text-right text-yellow-400 whitespace-nowrap">{formatMontant(data.total_montant)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
